import { LegalShell } from "./LegalShell";

export default function Privacy() {
  return (
    <LegalShell
      title="Privacy Policy"
      subtitle="What we collect, why, and how we keep it safe. Written in plain English — no legalese."
      path="/privacy"
      updated="February 12, 2026"
      sections={[
        {
          id: "who-we-are",
          heading: "Who we are",
          body: (
            <>
              <p>
                Heather's Boutique is a family-owned formalwear boutique headquartered in Oconomowoc, Wisconsin.
                We operate this website (heathersboutique.com) and are the "data controller" for the information
                you provide to us here.
              </p>
              <p>
                This policy explains what personal information we collect, how we use it, who we share it with,
                and the rights you have to control it. If anything on this page isn't clear, call the store —
                we'll answer.
              </p>
            </>
          ),
        },
        {
          id: "what-we-collect",
          heading: "What we collect",
          body: (
            <>
              <p><b>You give us:</b> name, email, phone, mailing address, ZIP, school affiliation, dress size,
              body measurements when submitted, photos you upload for AI try-on, order history, and payment
              details (payment details are handled by Stripe — we never see your card number).</p>
              <p><b>Automatically collected:</b> IP address, browser type, pages visited, referring URL, and
              anonymized usage events (via PostHog analytics) so we can improve the site.</p>
              <p><b>Cookies:</b> we use a small number of first-party cookies to keep you logged in and remember
              your bag. We do not sell third-party advertising cookies.</p>
            </>
          ),
        },
        {
          id: "why-we-collect",
          heading: "Why we collect it",
          body: (
            <>
              <p>We use your information to (1) run your orders and deliver your dresses, (2) send order status
              updates and one-off SMS reminders about your locked look, (3) power your AI Try-On, (4) prevent
              fraud and secure your account, (5) improve our fit recommendations, and (6) with your explicit
              opt-in, send occasional marketing about new arrivals and promotions.</p>
              <p>We <em>never</em> use your information to train third-party AI models or sell your data to
              anyone. Ever.</p>
            </>
          ),
        },
        {
          id: "sharing",
          heading: "Who we share it with",
          body: (
            <>
              <p>We share only what's necessary with a short list of trusted service providers so we can operate:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li><b>Stripe</b> — processes your payments (they never share your card details with us).</li>
                <li><b>Google Gemini</b> — generates your AI Try-On images. Photos you upload are sent to Google
                for the purpose of generating your result and are governed by Google's Gemini terms.</li>
                <li><b>Postmark / your SMTP provider</b> — delivers your order confirmation emails.</li>
                <li><b>GoHighLevel (GHL)</b> — sends transactional SMS messages you've explicitly opted into.</li>
                <li><b>PostHog</b> — records anonymized product analytics.</li>
              </ul>
              <p>We do not sell your personal information. We share information with law enforcement only when
              required by valid legal process.</p>
            </>
          ),
        },
        {
          id: "your-rights",
          heading: "Your rights",
          body: (
            <>
              <p>You can, at any time:</p>
              <ul className="list-disc pl-5 space-y-1.5">
                <li>Request a copy of all personal data we hold about you</li>
                <li>Ask us to correct or delete your account and all associated data</li>
                <li>Opt out of marketing emails (link in every marketing email footer) or SMS (reply STOP)</li>
                <li>Withdraw consent to AI Try-On uploads and have your uploaded photos deleted</li>
              </ul>
              <p>Email <a className="text-rosemetal" href="mailto:support@heathersboutique.com">support@heathersboutique.com</a> or
              call the store and we'll process your request within 30 days.</p>
            </>
          ),
        },
        {
          id: "minors",
          heading: "Minors",
          body: (
            <>
              <p>Many of our customers are high-school students under 18. We collect only the minimum data needed
              to serve them (name, email, size, school). If a customer is under 13 we require a parent to place
              the order and hold the account. Parents can request removal of any data about a minor by
              contacting the store directly.</p>
            </>
          ),
        },
        {
          id: "retention",
          heading: "Data retention",
          body: (
            <>
              <p>Order records are retained for 7 years (required for tax + warranty purposes). AI Try-On result
              images are retained for 90 days from generation, unless you've shared them to the community
              lookbook (then they persist until you delete them). You can delete your account anytime, which
              removes your profile, sizes, and non-tax data.</p>
            </>
          ),
        },
        {
          id: "security",
          heading: "How we keep it safe",
          body: (
            <>
              <p>All traffic is served over HTTPS (TLS 1.2+). Passwords are hashed with bcrypt. Card numbers
              never touch our servers. Access to customer data is limited to Heather and a small operations
              team, and all administrative access is logged.</p>
              <p>If we ever suffer a data breach affecting your personal information, we will notify you within
              72 hours of discovery, as required by Wisconsin law.</p>
            </>
          ),
        },
        {
          id: "changes",
          heading: "Changes to this policy",
          body: (
            <p>We may update this policy from time to time. The "Last Updated" date at the top always reflects
            the current version. Material changes will be announced via email to registered users at least
            14 days before taking effect.</p>
          ),
        },
      ]}
    />
  );
}
